/**
 * Authentication Service
 * Centralized service for all authentication-related API calls
 */

import { apiRequest } from '../utils/apiResponseHandler';
import { getAuthUrl } from '../config/api';

const authService = {
  // User Registration
  async register(userData) {
    const registrationData = {
      email: userData.email,
      password: userData.password,
      password_confirm: userData.passwordConfirm || userData.password_confirm
    };

    return await apiRequest(getAuthUrl('REGISTER'), {
      method: 'POST',
      body: registrationData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // User Login - Enhanced with proper error handling
  async login(credentials) {
    try {
      const response = await apiRequest(getAuthUrl('LOGIN'), {
        method: 'POST',
        body: {
          email: credentials.email,
          password: credentials.password
        },
        showSuccessMessage: false,
        showErrorMessage: false
      });

      // Handle successful login with proper session setup
      if (response.success && response.data) {
        // Extract tokens and user from response.data
        const { access, refresh, user } = response.data;

        // Validate that we have the required data
        if (!access || !refresh || !user) {
          console.error('Login response missing required fields:', response.data);
          return {
            success: false,
            message: 'Invalid login response from server',
            error: 'Missing required authentication data'
          };
        }

        // Store session data
        this.setSession({
          access,
          refresh,
          user,
          isAuthenticated: true
        });

        return {
          success: true,
          data: response.data,
          message: 'Login successful'
        };
      }

      // Handle error response
      if (!response.success) {
        // Extract error message from various possible locations in the response
        let errorMessage = response.message || response.error;
        
        // Check if error is in data field
        if (!errorMessage && response.data) {
          if (typeof response.data === 'string') {
            errorMessage = response.data;
          } else if (response.data.non_field_errors) {
            errorMessage = Array.isArray(response.data.non_field_errors) 
              ? response.data.non_field_errors[0] 
              : response.data.non_field_errors;
          } else if (response.data.error) {
            errorMessage = typeof response.data.error === 'string' 
              ? response.data.error 
              : (Array.isArray(response.data.error) ? response.data.error[0] : response.data.error.toString());
          } else if (response.data.message) {
            errorMessage = response.data.message;
          }
        }
        
        // Fallback to default message
        if (!errorMessage) {
          errorMessage = 'Authentication failed. Please check your credentials.';
        }
        
        return {
          success: false,
          message: errorMessage,
          error: errorMessage
        };
      }

      return response;
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: 'Login failed. Please try again.',
        error: error.message || 'Network error'
      };
    }
  },

  // Email Verification
  async verifyEmail(token) {
    return await apiRequest(`${getAuthUrl('VERIFY_EMAIL')}?token=${token}`, {
      method: 'GET',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },


  // Password Reset Request
  async requestPasswordReset(email) {
    return await apiRequest(getAuthUrl('PASSWORD_RESET_REQUEST'), {
      method: 'POST',
      body: { email },
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Password Reset Token Validation
  async validatePasswordResetToken(uid, token) {
    return await apiRequest(`${getAuthUrl('PASSWORD_RESET_VALIDATE')}?uid=${uid}&token=${token}`, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get Password Reset Info (for frontend redirect)
  async getPasswordResetInfo(uid, token) {
    return await apiRequest(`${getAuthUrl('PASSWORD_RESET')}?uid=${uid}&token=${token}`, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Password Reset
  async resetPassword(uid, token, newPassword, passwordConfirm) {
    return await apiRequest(`${getAuthUrl('PASSWORD_RESET')}?uid=${uid}&token=${token}`, {
      method: 'POST',
      body: {
        new_password: newPassword,
        password_confirm: passwordConfirm
      },
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Token Refresh
  async refreshToken(refreshToken) {
    return await apiRequest(getAuthUrl('TOKEN_REFRESH'), {
      method: 'POST',
      body: { refresh: refreshToken },
      showSuccessMessage: false,
      showErrorMessage: false
    });
  },

  // User Logout
  async logout(refreshToken) {
    return await apiRequest(getAuthUrl('LOGOUT'), {
      method: 'POST',
      body: { refresh: refreshToken },
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // User Profile
  async getProfile() {
    return await apiRequest(getAuthUrl('PROFILE'), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  async updateProfile(profileData) {
    return await apiRequest(getAuthUrl('PROFILE'), {
      method: 'PATCH',
      body: profileData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Admin - Get All Users
  async getUsers(filters = {}) {
    const queryParams = new URLSearchParams();
    if (filters.approval_status) queryParams.append('approval_status', filters.approval_status);
    if (filters.user_role) queryParams.append('user_role', filters.user_role);
    if (filters.search) queryParams.append('search', filters.search);

    const url = queryParams.toString()
      ? `${getAuthUrl('USERS')}?${queryParams.toString()}`
      : getAuthUrl('USERS');

    const token = localStorage.getItem('access_token');
    if (!token) {
      return {
        success: false,
        error: 'No authentication token found. Please log in again.',
        message: 'Authentication required'
      };
    }

    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Admin - Get User by ID
  async getUserById(userId) {
    return await apiRequest(getAuthUrl('USER_BY_ID', userId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Admin - Create User
  async createUser(userData) {
    return await apiRequest(getAuthUrl('USERS_CREATE'), {
      method: 'POST',
      body: userData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Admin - Update User
  async updateUser(userId, userData) {
    const endpoint = getAuthUrl('USERS_UPDATE', userId);
    return await apiRequest(endpoint, {
      method: 'PATCH',
      body: userData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Admin - Delete User
  async deleteUser(userId) {
    return await apiRequest(getAuthUrl('USERS_DELETE', userId), {
      method: 'DELETE',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Admin - Bulk User Actions
  async bulkUserAction(userIds, action, reason = '') {
    return await apiRequest(getAuthUrl('USERS_BULK_ACTION'), {
      method: 'POST',
      body: {
        user_ids: userIds,
        action: action,
        reason: reason
      },
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Admin - Approve/Reject User
  async approveUser(userId, isApproved) {
    return await apiRequest(getAuthUrl('USER_APPROVE', userId), {
      method: 'PUT',
      body: { is_approved: isApproved },
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Admin - Advanced User Search
  async searchUsers(searchParams = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(searchParams).forEach(key => {
      if (searchParams[key] !== undefined && searchParams[key] !== '') {
        queryParams.append(key, searchParams[key]);
      }
    });

    const url = queryParams.toString()
      ? `${getAuthUrl('USERS_SEARCH')}?${queryParams.toString()}`
      : getAuthUrl('USERS_SEARCH');

    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },


  // Admin - Dashboard Analytics
  async getDashboardAnalytics() {
    return await apiRequest(getAuthUrl('DASHBOARD'), {
      method: 'GET',
      timeout: 15000,
      showSuccessMessage: false,
      showErrorMessage: false
    });
  },

  // Admin - Get Pending Users
  async getPendingUsers() {
    return await apiRequest(getAuthUrl('USERS_PENDING'), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },


  // Admin - Get User Approval Status
  async getUserApprovalStatus(userId) {
    return await apiRequest(getAuthUrl('USER_APPROVE', userId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },


  // Session Management
  setSession(sessionData) {
    try {
      localStorage.setItem('user', JSON.stringify(sessionData));
      if (sessionData.access) {
        localStorage.setItem('access_token', sessionData.access);
      }
      if (sessionData.refresh) {
        localStorage.setItem('refresh_token', sessionData.refresh);
      }
    } catch (error) {
      // Session setting failed silently
    }
  },

  getCurrentSession() {
    try {
      const sessionData = JSON.parse(localStorage.getItem('user'));
      if (!sessionData || !sessionData.isAuthenticated) {
        return null;
      }

      // Ensure session has proper structure
      if (!sessionData.user || (!sessionData.user.role && !sessionData.user.user_role)) {
        this.clearSession();
        return null;
      }

      return sessionData;
    } catch (error) {
      this.clearSession();
      return null;
    }
  },

  clearSession() {
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    } catch (error) {
      // Session clearing failed silently
    }
  },


  // Token Management
  getAccessToken() {
    return localStorage.getItem('access_token');
  },

  getRefreshToken() {
    return localStorage.getItem('refresh_token');
  },

  // Check if user is authenticated
  isAuthenticated() {
    const session = this.getCurrentSession();
    return session && session.isAuthenticated;
  },

  // Get current user
  getCurrentUser() {
    const session = this.getCurrentSession();
    return session ? session.user : null;
  },

  // Helper methods for user status checking
  needsEmailVerification(user) {
    return user && !user.is_email_verified;
  },

  needsApproval(user) {
    return user && !user.is_approved && !user.is_superuser;
  },

  canLogin(user) {
    if (!user) return false;
    if (user.is_superuser) return true;
    return user.is_email_verified && user.is_approved;
  },

  // Map backend user role to frontend role
  mapUserRole(backendRole) {
    const roleMap = {
      'Superuser': 'admin',
      'Staff': 'admin',
      'Regular User': 'user'
    };
    return roleMap[backendRole] || 'user';
  },

  // Check if user can login based on API response
  canUserLogin(user) {
    if (!user) return false;

    // Superusers can always login
    if (user.is_superuser || user.user_role === 'Superuser') return true;

    // Check login status from API response
    if (user.login_status) {
      return user.login_status.can_login;
    }

    // Fallback to individual checks
    return user.is_email_verified && user.is_approved;
  },

  // Get user status message
  getUserStatusMessage(user) {
    if (!user) return 'User not found';

    if (user.login_status && user.login_status.status_message) {
      return user.login_status.status_message;
    }

    if (!user.is_email_verified) return 'Email verification required';
    if (!user.is_approved) return 'Admin approval required';
    return 'Account active and ready';
  },

  // Setup session with proper error handling
  async setupSession(loginResponse) {
    if (loginResponse.success && loginResponse.data) {
      const { access, refresh, user } = loginResponse.data;

      const sessionData = {
        access,
        refresh,
        user,
        isAuthenticated: true
      };

      this.setSession(sessionData);
      return sessionData;
    }

    throw new Error('Invalid login response');
  }
};

export { authService };
export default authService;